import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import queryString from 'query-string';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CompanyMeasurementDate } from 'api';
import { MEASUREMENT_DATE_TITLE } from 'common/constants/allocations';
import { DATE_ALIAS } from 'common/constants/general';
import { MEASUREMENT_DATE_ACTION_ADD, MEASUREMENT_DATE_ACTION_DELETE } from 'common/constants/measurement-date';
import { MEASUREMENT_DATE_FILTER } from 'common/constants/pageFilters';
import { useStore } from 'common/store';
import { MeasurementDate, SelectedMeasurementDate } from 'common/types/measurementDate';
import { UseStoreValues } from 'common/types/store';
import { DeleteMeasurementDateDialog } from 'components/DeleteMeasurementDate';
import { MeasurementDateDialog } from 'components/MeasurementDates';
import LayoutContext from 'context/LayoutContext';
import { ValuationsParams } from 'pages/ValuationsAllocation/types';
import { useGetMeasurementDatesByCompanyId } from 'services/hooks/firm';
import { extractDate, formatMeasurementDates, setQueryString } from 'utilities';

const useCompanyMeasurementDateFilter = () => {
  const [{ companyInfo }] = useStore() as unknown as UseStoreValues;

  const {
    setPageFilters,
    pageFilters,
    commonMeasurementDate,
    setCurrentMeasurementDate,
    setCommonMeasurementDate,
  } = useContext(LayoutContext);

  const { companySlugParam } = useParams<ValuationsParams>();

  const isSameCompany = (companyInfo?.slug && companySlugParam && companySlugParam === companyInfo.slug) || false;
  // Only true when we have both slugs but they don't match (actual company switch, not initial load)
  const isCompanyMismatch = companyInfo?.slug && companySlugParam && companySlugParam !== companyInfo.slug;

  const [measurementDates, fetchMeasurementDates] = useGetMeasurementDatesByCompanyId();

  const [selectedMeasurementDate, setSelectedMeasurementDate] = useState<MeasurementDate | undefined>();
  const [mdHasBeenDeleted, setMdHasBeenDeleted] = useState(false);
  const [openMeasurementDatesDialog, setOpenMeasurementDatesDialog] = useState(false);
  const [measurementDateAction, setMeasurementDateAction] = useState(MEASUREMENT_DATE_ACTION_ADD);
  const [newMD, setNewMD] = useState<string | undefined>();
  const [changedMd, setChangedMd] = useState<string | undefined>();
  const [pageInitialized, setPageInitialized] = useState(false);
  const previousCompanySlugRef = useRef<string | undefined>();

  const navigate = useNavigate();
  const location = useLocation();
  const [parsedQuery, setParsedQuery] = useState(queryString.parse(location.search));

  const { date: dateQueryString } = useMemo(() => parsedQuery, [parsedQuery]);

  const matchDateResult = (measurementDateList: MeasurementDate[], md: string) =>
    measurementDateList.find(item => [item?.id?.toString(), item.slug, item.date].includes(md?.toString()));

  const matchDate = useCallback(
    md => {
      if (!md || !measurementDates) {
        return undefined;
      }

      const mdToMatch = extractDate({
        dateSlug: md,
      });

      return matchDateResult(measurementDates as MeasurementDate[], mdToMatch);
    },
    [measurementDates]
  );

  const handleDateQueryString = useCallback(
    (dateArg, isNewMD) => {
      if (isEmpty(measurementDates) || mdHasBeenDeleted || isCompanyMismatch) {
        return;
      }

      const matchedDate
        = matchDate(dateArg)
        || matchDate(dateQueryString)
        || matchDate(commonMeasurementDate)
        || matchDate(companyInfo?.measurement_date_slug);

      const fallbackDate =
        matchedDate
        || (measurementDates as MeasurementDate[])?.[0];

      if (fallbackDate && fallbackDate.id !== selectedMeasurementDate?.id) {
        setSelectedMeasurementDate(fallbackDate);

        if (fallbackDate.slug !== parsedQuery.date) {
          setQueryString({
            navigate,
            pathname: location.pathname,
            searchParams: queryString.stringify(parsedQuery),
            paramName: DATE_ALIAS,
            paramValue: fallbackDate.slug,
            isNewMD,
          });
        }
      }
    },
    [
      mdHasBeenDeleted,
      selectedMeasurementDate,
      commonMeasurementDate,
      measurementDates,
      matchDate,
      dateQueryString,
      companyInfo?.measurement_date_slug,
      navigate,
      location.pathname,
      parsedQuery,
      isCompanyMismatch,
    ]
  );
  // Reset selected measurement date when switching companies so we don't reuse stale IDs
  useEffect(() => {
    const hasCompanySwitched = companySlugParam && previousCompanySlugRef.current && previousCompanySlugRef.current !== companySlugParam;

    if (hasCompanySwitched || isCompanyMismatch) {
      setSelectedMeasurementDate(undefined);
      setCurrentMeasurementDate?.(null);
      setCommonMeasurementDate?.(undefined as unknown as string);
      setParsedQuery(queryString.parse(location.search));
    }
    previousCompanySlugRef.current = companySlugParam;
  }, [companySlugParam, location.search, setCurrentMeasurementDate, setCommonMeasurementDate, isCompanyMismatch]);
  // Filter handlers
  const onMeasurementDateChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
      const measurementId = event.target.value;

      if (measurementId) {
        const selectedDate = ((measurementDates as MeasurementDate[]) ?? []).find(
          date => date?.id?.toString() === measurementId.toString()
        );

        delete parsedQuery.version;

        if (selectedDate) {
          setChangedMd(selectedDate.slug);
        }
      }
    },
    [measurementDates, parsedQuery.version]
  );

  const openMeasurementDateDialog = useCallback(
    (action = MEASUREMENT_DATE_ACTION_ADD) => {
      setOpenMeasurementDatesDialog(true);
      setMeasurementDateAction(action);
    },
    [setMeasurementDateAction]
  );

  const getMeasurementDates = useCallback(
    async (newCompanyMeasurementDate?: CompanyMeasurementDate | undefined) => {
      if (!isEmpty(companyInfo)) {
        await fetchMeasurementDates(companyInfo?.id as number);

        if (!newCompanyMeasurementDate) {
          setPageInitialized(false);
        }
      }

      const recentMD = newCompanyMeasurementDate?.measurement_date ?? false;

      if (recentMD) {
        setNewMD(recentMD.slug);
      }
    },
    [companyInfo, fetchMeasurementDates]
  );

  const clearFiltersFromUrl = useCallback(() => {
    setCurrentMeasurementDate?.(null);
    navigate(location.pathname);
    setMdHasBeenDeleted(false);
  }, [navigate, location.pathname, setCurrentMeasurementDate]);

  // If location.search.date changes from its previous value
  useEffect(() => {
    const tmpParsedQuery = queryString.parse(location.search);
    if ((tmpParsedQuery.date !== parsedQuery.date || tmpParsedQuery.tab !== parsedQuery.tab) && isSameCompany) {
      setParsedQuery(tmpParsedQuery);
    }
  }, [location, parsedQuery, isSameCompany]);

  // When the selected measurement date changes
  useEffect(() => {
    setCurrentMeasurementDate?.(selectedMeasurementDate as SelectedMeasurementDate);
  }, [selectedMeasurementDate, setCurrentMeasurementDate]);

  // When a new measurement date is added
  useEffect(() => {
    if (newMD && !isEmpty(measurementDates) && (measurementDates as MeasurementDate[]).find(md => md.slug === newMD)) {
      handleDateQueryString(newMD, true);
      setNewMD('');
    }
  }, [newMD, measurementDates, handleDateQueryString]);

  // When the measurement date changes
  useEffect(() => {
    if (
      changedMd
      && !isEmpty(measurementDates)
      && (measurementDates as MeasurementDate[]).find(md => md.slug === changedMd)
    ) {
      handleDateQueryString(changedMd, false);
      setChangedMd('');
    }
  }, [changedMd, measurementDates, handleDateQueryString]);

  // Get measurement Dates
  useEffect(() => {
    if (!isEmpty(companyInfo) && isSameCompany) {
      getMeasurementDates().catch(() => { });
    }
  }, [companyInfo, fetchMeasurementDates, getMeasurementDates, isSameCompany]);

  // When the page loads
  useEffect(() => {
    if (!pageInitialized && !isEmpty(measurementDates) && !mdHasBeenDeleted) {
      handleDateQueryString(undefined, false);
      setPageInitialized(true);
    }
  }, [measurementDates, mdHasBeenDeleted, handleDateQueryString, pageInitialized]);

  // When a measurement date has been deleted
  useEffect(() => {
    if (mdHasBeenDeleted) {
      clearFiltersFromUrl();
    }
  }, [measurementDates, mdHasBeenDeleted, clearFiltersFromUrl]);

  // When the company changes
  useEffect(() => {
    if (!isSameCompany) {
      setPageInitialized(false);
    }
  }, [isSameCompany]);

  const isAddingNewMD = measurementDateAction === MEASUREMENT_DATE_ACTION_ADD;

  const MDDialogue = React.memo(() => (
    <>
      {openMeasurementDatesDialog && isAddingNewMD && (
        <MeasurementDateDialog
          open={openMeasurementDatesDialog}
          // defaultCompanies={companyInfo ? { [companyInfo.id as number]: true } : undefined}
          getMeasurementDates={getMeasurementDates}
          onClose={() => setOpenMeasurementDatesDialog(false)}
          activeCompany={companyInfo}
        />
      )}
      {openMeasurementDatesDialog && !isAddingNewMD && (
        <DeleteMeasurementDateDialog
          open={openMeasurementDatesDialog}
          onClose={() => setOpenMeasurementDatesDialog(false)}
          getMeasurementDates={getMeasurementDates}
          setMdHasBeenDeleted={setMdHasBeenDeleted}
          selectedCmdId={selectedMeasurementDate?.cmd_id as number | undefined}
        />
      )}
    </>
  ));

  // Memoize the filter object to prevent recreation
  const measurementDateFilter = useMemo(() => {
    if (!isEmpty(measurementDates) && selectedMeasurementDate) {
      return {
        id: MEASUREMENT_DATE_FILTER,
        title: MEASUREMENT_DATE_TITLE,
        options: formatMeasurementDates(measurementDates ?? []),
        selectedValue: selectedMeasurementDate?.id,
        handler: onMeasurementDateChange,
        addOption: () => openMeasurementDateDialog(MEASUREMENT_DATE_ACTION_ADD),
        deleteOption: () => openMeasurementDateDialog(MEASUREMENT_DATE_ACTION_DELETE),
      };
    }
    return null;
  }, [selectedMeasurementDate, measurementDates, onMeasurementDateChange, openMeasurementDateDialog]);

  useEffect(() => {
    // Check if update is needed by reading current pageFilters state
    const currentMeasurementDateFilter = pageFilters?.find(filter => filter.id === MEASUREMENT_DATE_FILTER);
    const needsUpdate
      = !currentMeasurementDateFilter
      || currentMeasurementDateFilter.selectedValue !== selectedMeasurementDate?.id
      || mdHasBeenDeleted;

    if (measurementDateFilter && needsUpdate) {
      setPageFilters?.([measurementDateFilter]);
    } else if (!measurementDateFilter) {
      setPageFilters?.([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedMeasurementDate,
    measurementDates,
    setPageFilters,
    measurementDateFilter,
    mdHasBeenDeleted,
  ]);

  return {
    selectedMeasurementDate,
    MDDialogue,
    openMeasurementDateDialog,
    matchDate,
    measurementDates,
  };
};

export default useCompanyMeasurementDateFilter;
